import { DrawerDiaLog } from '../drawer-dialog';
import { SpanLoading } from '../fetch-status/span-loading';
import { Button } from '../ui/button';
import { InteractCloseIcon } from '@/client/assets';
import { useAuth } from '@/client/hooks/use-auth';
import { cn } from '@/client/utils';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const MethodRow = ({ icon, title, subtitle, onClick }: { icon: ReactNode; title: string; subtitle?: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-start',
      'bg-secondary/60 border border-border hover:border-border-bright hover:shadow-card-glow',
      'transition-all duration-200'
    )}
  >
    <span className="size-9 shrink-0 flex items-center justify-center rounded-lg bg-background/60">{icon}</span>
    <span className="flex flex-col">
      <span className="text-sm font-semibold text-foreground">{title}</span>
      {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
    </span>
  </button>
);

const WalletIcon = () => (
  <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
    <path d="M16 12h.01M3 9h18" />
  </svg>
);

export const LoginMethodModel = ({ open, setOpen }: Props) => {
  const { t } = useTranslation();
  const { loginWallet, isAuthLoading } = useAuth();
  const close = () => setOpen(false);

  return (
    <DrawerDiaLog open={open} setOpen={setOpen} className="w-full md:max-w-sm border border-foreground/4">
      <div className="px-4 pb-6 md:p-5 space-y-5">
        <div className="flex items-center justify-between">
          <dl className="space-y-1">
            <dt className="text-lg leading-6 font-display font-semibold text-foreground">{t('log-in', 'Log in')}</dt>
            <dd className="text-xs text-muted-foreground">{t('connect-wallet-to-continue', 'Connect your wallet to continue')}</dd>
          </dl>
          <Button size="icon" variant="ghost" className="hover:scale-90 transition" onClick={close}>
            <InteractCloseIcon className="!size-6 fill-foreground/40" />
          </Button>
        </div>

        {/* Wallet is the only sign-in path on this tenant: no DID/social login is
            provisioned, so offering it would dead-end the user. */}
        <div className="space-y-3">
          <MethodRow
            icon={<WalletIcon />}
            title={t('connect-wallet', 'Connect Wallet')}
            subtitle={t('wallet-login-subtitle', 'Use your own wallet to trade directly')}
            onClick={() => {
              close();
              loginWallet?.();
            }}
          />
        </div>

        {isAuthLoading && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <SpanLoading /> {t('signing-in', 'Signing in…')}
          </div>
        )}

        <p className="text-[11px] leading-relaxed text-muted-foreground/70 text-center">
          {t('login-terms-hint', 'By signing in you agree to the Terms of Use.')}
        </p>
      </div>
    </DrawerDiaLog>
  );
};

export default LoginMethodModel;
